import { useCallback, useMemo } from 'react';
import { OrderManagementResource } from '../../resources';
import { TradeType } from '../../types';
import { ValidatedOrder } from '../../types/order/validatedOrder';
import { GetResourceHeaderWithTokenForAccount } from '../useResource';
import { mapValidatedOrderToOrderRequest } from './orderUtils';

export type UseOrderCostCalculationCommonPlatformParams = {
  orderManagementResource: OrderManagementResource;
  getResourceHeaderWithTokenForAccount: GetResourceHeaderWithTokenForAccount;
};

export type OrderCostCalculationResult = {
  buyAmount: number;
  sellAmount: number;
  amountByTradeType: number;
};

export type UseOrderCostCalculationOutput = {
  calculateOrderCost: (order: ValidatedOrder, accountId: string) => Promise<OrderCostCalculationResult>;
};

export const useOrderCostCalculationCommon = ({
  orderManagementResource,
  getResourceHeaderWithTokenForAccount,
}: UseOrderCostCalculationCommonPlatformParams): UseOrderCostCalculationOutput => {
  const calculateOrderCost = useCallback(
    async (order: ValidatedOrder, accountId: string): Promise<OrderCostCalculationResult> => {
      const orderRequest = mapValidatedOrderToOrderRequest(order, undefined);
      const response = await orderManagementResource.costCalculation(orderRequest, {
        ...getResourceHeaderWithTokenForAccount(accountId),
        accountId,
      });

      const buyAmount = response.data.buyAmount ?? 0;
      const sellAmount = response.data.sellAmount ?? 0;
      const amountByTradeType = order.tradeType === TradeType.Buy ? buyAmount : sellAmount;

      return {
        buyAmount,
        sellAmount,
        amountByTradeType,
      };
    },
    [getResourceHeaderWithTokenForAccount, orderManagementResource],
  );

  return useMemo<UseOrderCostCalculationOutput>(
    () => ({
      calculateOrderCost,
    }),
    [calculateOrderCost],
  );
};
